import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

const MATCH_TYPES = ['contains', 'exact', 'starts_with'];

export default function AIBot() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ bot_enabled: 0, start_time: '09:00', end_time: '18:00', welcome_message: 'Hi {name}, How can I help you?', fallback_message: 'I am not sure about that. Please wait for an agent.', outside_hours_message: 'We are closed. We will reply soon!' });
  const [keywords, setKeywords] = useState([]);
  const [form, setForm] = useState({ keyword: '', match_type: 'contains', reply: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAdvanced = user?.plan_key === 'pack_advanced';

  useEffect(() => {
    if (!isAdvanced) return;
    axios.get('/api/business/ai-bot/settings').then(r => { if (r.data.data) setSettings(r.data.data); }).catch(() => {});
    axios.get('/api/business/ai-bot/keywords').then(r => setKeywords(r.data.data || [])).catch(() => {});
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put('/api/business/ai-bot/settings', settings);
      toast.success('Settings saved!');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const addKeyword = async () => {
    if (!form.keyword.trim() || !form.reply.trim()) return toast.error('Fill keyword and reply');
    setLoading(true);
    try {
      await axios.post('/api/business/ai-bot/keywords', form);
      toast.success('Keyword added!');
      setForm({ keyword: '', match_type: 'contains', reply: '' });
      axios.get('/api/business/ai-bot/keywords').then(r => setKeywords(r.data.data || []));
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const toggleKeyword = async (kw) => {
    await axios.put(`/api/business/ai-bot/keywords/${kw.id}`, { ...kw, is_active: kw.is_active ? 0 : 1 });
    axios.get('/api/business/ai-bot/keywords').then(r => setKeywords(r.data.data || []));
  };

  const deleteKeyword = async (id) => {
    await axios.delete(`/api/business/ai-bot/keywords/${id}`);
    setKeywords(k => k.filter(x => x.id !== id));
    toast.success('Deleted');
  };

  if (!isAdvanced) {
    return (
      <DashLayout navItems={nav} role="Business Admin">
        <div style={{ padding: 28, textAlign: 'center', paddingTop: 80 }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>🤖</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#a78bfa', marginBottom: 8 }}>AI Chat Bot</h2>
          <p style={{ color: '#555', fontSize: 14, marginBottom: 24 }}>AI Chat Bot is available in Pack Advanced only.</p>
          <a href="/dashboard/billing" style={{ display: 'inline-block', padding: '12px 28px', background: '#7c3aed', color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none' }}>Upgrade to Pack Advanced →</a>
        </div>
      </DashLayout>
    );
  }

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>🤖 AI Chat Bot</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 28 }}>Set up keyword-based auto replies for your chat widget</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          {/* Bot Settings */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 20 }}>⚙️ Bot Settings</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Enable toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#0d0d0d', borderRadius: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Bot Status</div>
                  <div style={{ fontSize: 12, color: '#555' }}>{settings.bot_enabled ? '🟢 Active' : '⚫ Disabled'}</div>
                </div>
                <div onClick={() => setSettings(s => ({ ...s, bot_enabled: s.bot_enabled ? 0 : 1 }))}
                  style={{ width: 44, height: 24, borderRadius: 12, background: settings.bot_enabled ? '#7c3aed' : '#333', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                  <div style={{ position: 'absolute', top: 3, left: settings.bot_enabled ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </div>
              </div>
              {/* Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Start Time</label>
                  <input type="time" value={settings.start_time} onChange={e => setSettings(s => ({ ...s, start_time: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>End Time</label>
                  <input type="time" value={settings.end_time} onChange={e => setSettings(s => ({ ...s, end_time: e.target.value }))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Welcome Message <span style={{ color: '#555' }}>(use {'{name}'} for visitor name)</span></label>
                <textarea value={settings.welcome_message} onChange={e => setSettings(s => ({ ...s, welcome_message: e.target.value }))} rows={2} style={{ resize: 'vertical' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Fallback (no keyword match)</label>
                <textarea value={settings.fallback_message} onChange={e => setSettings(s => ({ ...s, fallback_message: e.target.value }))} rows={2} style={{ resize: 'vertical' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Outside Hours Message</label>
                <textarea value={settings.outside_hours_message} onChange={e => setSettings(s => ({ ...s, outside_hours_message: e.target.value }))} rows={2} style={{ resize: 'vertical' }} />
              </div>
              <button onClick={saveSettings} disabled={saving}
                style={{ padding: '11px', background: '#7c3aed', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : '💾 Save Settings'}
              </button>
            </div>
          </div>

          {/* Add keyword */}
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 20 }}>+ Add Keyword Reply</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Keyword *</label>
                <input value={form.keyword} onChange={e => setForm(f => ({ ...f, keyword: e.target.value }))} placeholder="e.g. price, hello, location" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Match Type</label>
                <select value={form.match_type} onChange={e => setForm(f => ({ ...f, match_type: e.target.value }))}>
                  <option value="contains">Contains (best)</option>
                  <option value="exact">Exact match</option>
                  <option value="starts_with">Starts with</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Reply Message *</label>
                <textarea value={form.reply} onChange={e => setForm(f => ({ ...f, reply: e.target.value }))} placeholder="Auto reply for this keyword..." rows={4} style={{ resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#555' }}>💡 Quick Examples:</span>
                {['price', 'hello', 'location', 'timing', 'support'].map(k => (
                  <button key={k} onClick={() => setForm(f => ({ ...f, keyword: k }))}
                    style={{ fontSize: 11, padding: '2px 10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 20, cursor: 'pointer' }}>
                    {k}
                  </button>
                ))}
              </div>
              <button onClick={addKeyword} disabled={loading}
                style={{ padding: '11px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Adding...' : '+ Add Keyword'}
              </button>
            </div>
          </div>
        </div>

        {/* Keywords list */}
        {keywords.length > 0 && (
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0d0d0d', borderBottom: '1px solid #1e1e1e' }}>
                  {['KEYWORD', 'MATCH', 'REPLY', 'USED', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: '#555', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {keywords.map(kw => (
                  <tr key={kw.id} style={{ borderBottom: '1px solid #0d0d0d' }}>
                    <td style={{ padding: '12px 16px', color: '#00d55b', fontWeight: 600, fontSize: 13 }}>{kw.keyword}</td>
                    <td style={{ padding: '12px 16px', color: '#888', fontSize: 12 }}>{kw.match_type}</td>
                    <td style={{ padding: '12px 16px', color: '#888', fontSize: 12, maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kw.reply}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#555', fontSize: 12 }}>{kw.used_count || 0}x</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: kw.is_active ? 'rgba(0,213,91,0.1)' : '#1a1a1a', color: kw.is_active ? '#00d55b' : '#555', fontWeight: 600 }}>
                        {kw.is_active ? 'ACTIVE' : 'PAUSED'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => toggleKeyword(kw)}
                          style={{ padding: '4px 10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                          {kw.is_active ? '⏸' : '▶'}
                        </button>
                        <button onClick={() => deleteKeyword(kw.id)}
                          style={{ padding: '4px 10px', background: 'rgba(255,68,68,0.1)', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashLayout>
  );
}